import { ID } from 'types';

// NSB price-override permissions. Standalone (imports only `types`) so the column-header popover and
// the event-config menu can read/write it without pulling in the renderer/quick-picker import graph.
export type NsbOverrideColumn = 'baseline' | 'negotiation';

// ── Per-lot override permission ──────────────────────────────────────────────
// `${lotId}::${column}` → boolean. Default (absent) = false. Set via the lot column-header (i) toggle.
const lotOverrideStore = new Map<string, boolean>();
const overrideKey = (lotId: ID, column: NsbOverrideColumn): string => `${lotId}::${column}`;

// Raw per-lot value — used by the lot (i) toggle to show/set its OWN state.
export const getLotOverrideAllowed = (lotId: ID, column: NsbOverrideColumn): boolean =>
  lotOverrideStore.get(overrideKey(lotId, column)) ?? false;

// ── Event-level override permission (hamburger "Price Configuration") ────────
// ASSUMPTION pending UX confirmation (2026-06-05): the event-level toggle is an "all lots" master
// switch that OR-combines with each lot's toggle — overrides are allowed for a lot/column when the
// EVENT-level toggle is on OR that lot's own toggle is on. Both default OFF, evaluated per column.
const eventOverrideStore = new Map<NsbOverrideColumn, boolean>();

export const getEventOverrideAllowed = (column: NsbOverrideColumn): boolean =>
  eventOverrideStore.get(column) ?? false;

// EFFECTIVE permission used to gate row-level SB-flavor overrides (Apply actions). Now governed
// SOLELY by the event-level toggle (the per-lot toggle was removed). `lotId` is accepted for call-site
// compatibility but no longer used.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const isOverrideAllowed = (lotId: ID, column: NsbOverrideColumn): boolean =>
  getEventOverrideAllowed(column);

const overrideSubs = new Set<() => void>();

// Coalesced into a microtask, mirroring nsbRowConfigStore. Both cell renderers, the quick picker and
// the intel modal subscribe, so toggling a permission on a large event would otherwise fire a
// synchronous callback per cell. Any burst within a tick becomes one subscriber round.
let notifyScheduled = false;

// The one place subscribers are fired, and it owns the flag - see nsbSelectionStore for why both paths go
// through it rather than the sync path firing subscribers directly.
const flushNow = (): void => {
  notifyScheduled = false;
  overrideSubs.forEach(fn => fn());
};

const notify = (): void => {
  if (notifyScheduled) return;
  notifyScheduled = true;
  queueMicrotask(() => { if (notifyScheduled) flushNow(); });
};

export const setLotOverrideAllowed = (lotId: ID, column: NsbOverrideColumn, allowed: boolean): void => {
  lotOverrideStore.set(overrideKey(lotId, column), allowed);
  notify();
};

export const setEventOverrideAllowed = (column: NsbOverrideColumn, allowed: boolean): void => {
  eventOverrideStore.set(column, allowed);
  notify();
};

// Restore the event-level permission from ui_config.nsb on load. Separate from the setter above, and
// notifying IMMEDIATELY rather than on the coalesced path, because the two are not the same event:
// restoring a saved permission is not the buyer revoking one. LineItemsProvider.fetchEvent calls this
// and THEN hydrates each row's config in the same synchronous run, and BaselinePriceRenderer's handler
// drops a row's per-row pick whenever it observes the permission off. Deferred, that round lands after
// row hydration and deletes the pick that was just restored — the clobber hydration exists to prevent.
// Firing here keeps the pre-coalescing order, where the round ran while the pick maps were still empty.
export const hydrateEventOverrideAllowed = (column: NsbOverrideColumn, allowed: boolean): void => {
  eventOverrideStore.set(column, allowed);
  flushNow();
};

export const subscribeLotOverride = (fn: () => void): (() => void) => {
  overrideSubs.add(fn);
  return () => { overrideSubs.delete(fn); };
};

// Clear all override permissions (event + lot) on event change.
export const resetLotOverrides = (): void => {
  lotOverrideStore.clear();
  eventOverrideStore.clear();
  notify();
};
